import { useState, useEffect, useCallback } from "react";
import { avatarBg } from "@/lib/avatar";
import { useSwipe } from "@/lib/useSwipe";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { useAppMode } from "@/context/AppModeContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Heart, MapPin, MessageCircle, Calendar, Coins, ChevronLeft, ChevronRight, Crown, Zap, Gem, Sparkles, Play } from "lucide-react";
import Navigation from "@/components/Navigation";
import SendOfferModal from "@/components/SendOfferModal";
import AgeVerificationModal from "@/components/AgeVerificationModal";
import BoostDialog from "@/components/BoostDialog";
import { onRealtimeEvent } from "@/lib/realtime";
import { useLocale } from "@/i18n/LocaleContext";
import CompanionsRow from "@/components/CompanionsRow";
import SecretAdmirerPromo from "@/components/SecretAdmirerPromo";
import EventsPromoBanner from "@/components/EventsPromoBanner";

export default function Discover() {
  const navigate = useNavigate();
  const { t, lang } = useLocale();
  const { user } = useAuth();
  const { isPayer } = useAppMode();
  const [profiles, setProfiles] = useState([]);
  const [relationship, setRelationship] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sendOfferDialog, setSendOfferDialog] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [ageModalOpen, setAgeModalOpen] = useState(false);
  const [boostDialog, setBoostDialog] = useState(false);
  const [boostStatus, setBoostStatus] = useState(null);
  const [sortBy, setSortBy] = useState("newest");
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [trBio, setTrBio] = useState({});

  useEffect(() => {
    const p = profiles[currentIndex];
    const key = p ? `${lang}:${p.user_id}` : null;
    if (!p || lang === "en" || !key || trBio[key] !== undefined) return;
    const texts = [p.bio || "", ...(p.interests || []).slice(0, 4)];
    if (texts.every((x) => !x)) return;
    api.post("/translate", { texts, lang })
      .then((res) => {
        const tr = res.data.translations || [];
        setTrBio((m) => ({ ...m, [key]: { bio: tr[0] || p.bio, ints: tr.slice(1) } }));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profiles, currentIndex, lang]);

  useEffect(() => {
    api.get("/favorites/ids").then((res) => setFavoriteIds(res.data.ids)).catch(() => {});
  }, []);

  const toggleFavorite = async (targetId) => {
    try {
      const response = await api.post(`/favorites/${targetId}`);
      if (response.data.favorited) {
        setFavoriteIds((prev) => [...prev, targetId]);
        toast.success("Added to favorites ❤️");
      } else {
        setFavoriteIds((prev) => prev.filter((id) => id !== targetId));
        toast.success("Removed from favorites");
      }
    } catch (error) {
      toast.error("Failed to update favorites");
    }
  };

  const fetchBoostStatus = useCallback(async () => {
    try {
      const response = await api.get("/boost/status");
      setBoostStatus(response.data);
    } catch (error) {
      // silent - non-critical
    }
  }, []);

  useEffect(() => {
    if (!isPayer) return;
    fetchBoostStatus();
    const unsubscribe = onRealtimeEvent((event) => {
      if (event.type === "notification" && event.notification?.type === "boost_activated") {
        fetchBoostStatus();
      }
    });
    return unsubscribe;
  }, [isPayer, fetchBoostStatus]);

  const fetchProfiles = useCallback(async () => {
    try {
      const response = await api.get(`/discover?page=${page}&limit=10&sort=${sortBy}`);
      setProfiles(response.data.profiles);
      setTotalPages(response.data.pages);
      setRelationship(response.data.relationship || null);
    } catch (error) {
      console.error("Failed to load profiles:", error);
      toast.error("Failed to load profiles");
    } finally {
      setLoading(false);
    }
  }, [page, sortBy]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const currentProfile = profiles[currentIndex];

  const handleNext = () => {
    if (currentIndex < profiles.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else if (page < totalPages) {
      setPage(page + 1);
      setCurrentIndex(0);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else if (page > 1) {
      setPage(page - 1);
      setCurrentIndex(0);
    }
  };

  const handleViewProfile = (profileId) => {
    navigate(`/profile/${profileId}`);
  };

  const swipe = useSwipe({ onSwipeLeft: handleNext, onSwipeRight: handlePrevious });

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (relationship) {
    const partner = relationship.partner;
    const partnerPhoto = partner?.photos?.[0] || partner?.picture;
    return (
      <div className="min-h-screen bg-background" data-testid="discover-page">
        <Navigation />
        <main className="pt-20 pb-24 px-4 md:px-6">
          <div className="max-w-lg mx-auto">
            <div className="glass-card p-8 md:p-10 text-center border border-rose-500/25 mt-8" data-testid="couple-mode-card">
              {partnerPhoto && (
                <img src={partnerPhoto} alt={partner?.name} className="w-24 h-24 rounded-full object-cover mx-auto mb-5 ring-4 ring-rose-500/30" />
              )}
              <h1 className="text-2xl font-bold font-['Outfit'] tracking-tight mb-2">
                {t("rel.coupleTitle")}
              </h1>
              <p className="text-muted-foreground mb-6">
                {t("rel.coupleDesc").replace("{name}", partner?.name || "")}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  className="h-12 rounded-xl bg-primary hover:bg-primary/90"
                  onClick={() =>
                    relationship.conversation_id
                      ? navigate(`/chat/${relationship.conversation_id}`)
                      : navigate(`/profile/${partner?.user_id}?action=message`)
                  }
                  data-testid="couple-chat-btn"
                >
                  <MessageCircle className="w-4 h-4 mr-2" /> {t("rel.chat")}
                </Button>
                <Button
                  className="h-12 rounded-xl gradient-gold text-black font-semibold"
                  onClick={() => navigate(`/profile/${partner?.user_id}?action=meetup`)}
                  data-testid="couple-meetup-btn"
                >
                  <Calendar className="w-4 h-4 mr-2" /> {t("rel.planDate")}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-5">
                {t("rel.coupleHint")}
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="discover-page">
      <Navigation />
      
      <main className="pt-20 pb-24 px-4 md:px-6">
        <div className="max-w-lg mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold font-['Outfit'] tracking-tight">{t("discover.title")}</h1>
              <p className="text-muted-foreground text-sm">{t("discover.subtitle")}</p>
            </div>
            {isPayer && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  className={boostStatus?.active
                    ? "border-amber-500/50 text-amber-400 bg-amber-500/10 hover:bg-amber-500/20"
                    : "border-amber-500/30 text-amber-400 hover:bg-amber-500/10"}
                  onClick={() => setBoostDialog(true)}
                  data-testid="boost-profile-btn"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  {boostStatus?.active
                    ? `Boosted · ${Math.max(1, Math.ceil((new Date(boostStatus.boosted_until) - Date.now()) / 3600000))}h`
                    : "Boost $25"}
                </Button>
                <Button
                  variant="outline"
                  className="border-secondary/30 text-secondary hover:bg-secondary/10"
                  onClick={() => navigate("/credits")}
                  data-testid="credits-balance-btn"
                >
                  <Coins className="w-4 h-4 mr-2" />
                  {user?.credits || 0}
                </Button>
              </div>
            )}
          </div>

          {/* Sort toggle */}
          <div className="flex gap-2 mb-6" data-testid="discover-sort-toggle">
              <button
                type="button"
                className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                  sortBy === "newest"
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-white/10 bg-white/5 text-muted-foreground hover:bg-white/10"
                }`}
                onClick={() => { setSortBy("newest"); setPage(1); setCurrentIndex(0); }}
                data-testid="sort-newest-btn"
              >
                {t("discover.newest")}
              </button>
              <button
                type="button"
                className={`px-4 py-2 rounded-full text-sm border transition-colors flex items-center gap-1.5 ${
                  sortBy === "top_rated"
                    ? "border-amber-500/50 bg-amber-500/10 text-amber-400"
                    : "border-white/10 bg-white/5 text-muted-foreground hover:bg-white/10"
                }`}
                onClick={() => { setSortBy("top_rated"); setPage(1); setCurrentIndex(0); }}
                data-testid="sort-top-rated-btn"
              >
                ★ {t("discover.topRated")}
              </button>
            </div>

          <EventsPromoBanner />

          <CompanionsRow />

          <SecretAdmirerPromo />

          {profiles.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <Heart className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">No profiles yet</h2>
              <p className="text-muted-foreground">
                Check back soon for new matches!
              </p>
            </div>
          ) : currentProfile ? (
            <div className="relative">
              {/* Profile Card */}
              <div 
                className="relative h-[600px] w-full rounded-3xl overflow-hidden border border-white/10 cursor-pointer touch-pan-y"
                onClick={() => {
                  if (swipe.swiped.current) {
                    swipe.swiped.current = false;
                    return;
                  }
                  handleViewProfile(currentProfile.user_id);
                }}
                {...swipe.handlers}
                data-testid="profile-card"
              >
                <img
                  src={currentProfile.photos?.[0] || currentProfile.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(currentProfile.name)}&background=${avatarBg()}&color=fff&size=600`}
                  alt={currentProfile.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 profile-overlay" />

                {/* Favorite heart */}
                <button
                  type="button"
                  className="absolute top-4 left-4 w-11 h-11 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center hover:bg-black/60 transition-colors z-10"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavorite(currentProfile.user_id);
                  }}
                  data-testid="favorite-toggle-btn"
                >
                  <Heart
                    className={`w-6 h-6 transition-colors ${
                      favoriteIds.includes(currentProfile.user_id)
                        ? "text-primary fill-primary"
                        : "text-white"
                    }`}
                  />
                </button>
                
                {/* Profile info */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-end justify-between">
                    <div>
                      <h2 className="text-3xl font-bold font-['Outfit'] mb-1">
                        {currentProfile.name}{currentProfile.age ? `, ${currentProfile.age}` : ""}
                      </h2>
                      {currentProfile.location && (
                        <div className="flex items-center gap-1 text-white/70">
                          <MapPin className="w-4 h-4" />
                          <span>{currentProfile.location}</span>
                        </div>
                      )}
                      {currentProfile.bio && (
                        <p className="mt-2 text-white/80 line-clamp-2 max-w-xs">
                          {trBio[`${lang}:${currentProfile.user_id}`]?.bio || currentProfile.bio}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex flex-col items-end gap-2">
                      {currentProfile.is_ai && (
                        <div className="bg-violet-500/90 text-white rounded-full px-3 py-1 text-xs font-semibold flex items-center gap-1" data-testid="ai-companion-badge">
                          <Sparkles className="w-3 h-3" />
                          {t("discover.aiCompanion")}
                        </div>
                      )}
                      {currentProfile.videos?.length > 0 && (
                        <div className="bg-black/60 backdrop-blur-sm border border-white/20 rounded-full px-3 py-1 text-xs font-semibold flex items-center gap-1" data-testid="video-intro-badge">
                          <Play className="w-3 h-3 fill-current" />
                          {t("discover.videoIntro")}
                        </div>
                      )}
                      {currentProfile.is_boosted && (
                        <div className="bg-amber-500/90 text-black rounded-full px-3 py-1 text-xs font-semibold flex items-center gap-1" data-testid="boosted-badge">
                          <Zap className="w-3 h-3" />
                          {t("discover.boosted")}
                        </div>
                      )}
                      {currentProfile.diamond_badge && (
                        <div className="bg-cyan-500/20 border border-cyan-400/40 text-cyan-300 rounded-full px-3 py-1 text-xs font-semibold flex items-center gap-1" data-testid="diamond-badge">
                          <Gem className="w-3 h-3" /> {t("discover.diamond")}
                        </div>
                      )}
                      {currentProfile.fire_badge && (
                        <div className="bg-orange-500/20 border border-orange-400/40 text-orange-300 rounded-full px-3 py-1 text-xs font-semibold" data-testid="fire-badge">
                          {t("discover.inDemand")}
                        </div>
                      )}
                      {currentProfile.is_verified && (
                        <div className="bg-primary/90 rounded-full px-3 py-1 text-xs font-medium">
                          {t("discover.verifiedBadge")}
                        </div>
                      )}
                      {currentProfile.ratings_enabled !== false && currentProfile.review_count > 0 && (
                        <div className="bg-white/10 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-medium text-amber-400" data-testid="rating-badge">
                          ★ {Number(currentProfile.avg_rating).toFixed(1)} ({currentProfile.review_count})
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Interests */}
                  {currentProfile.interests?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {currentProfile.interests.slice(0, 4).map((interest, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm text-sm"
                        >
                          {trBio[`${lang}:${currentProfile.user_id}`]?.ints?.[index] || interest}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Navigation arrows */}
              <div className="flex justify-center gap-4 mt-6">
                <Button
                  variant="outline"
                  size="icon"
                  className="w-14 h-14 rounded-full border-white/10"
                  onClick={handlePrevious}
                  disabled={currentIndex === 0 && page === 1}
                  data-testid="prev-profile-btn"
                >
                  <ChevronLeft className="w-6 h-6" />
                </Button>

                <Button
                  className="w-14 h-14 rounded-full bg-primary hover:bg-primary/90 glow-primary"
                  onClick={() => handleViewProfile(currentProfile.user_id)}
                  data-testid="view-profile-btn"
                >
                  <Heart className="w-6 h-6" />
                </Button>

                <Button
                  variant="outline"
                  size="icon"
                  className="w-14 h-14 rounded-full border-white/10"
                  onClick={handleNext}
                  disabled={currentIndex === profiles.length - 1 && page === totalPages}
                  data-testid="next-profile-btn"
                >
                  <ChevronRight className="w-6 h-6" />
                </Button>
              </div>

              {/* Action buttons */}
              {currentProfile.is_ai && (
                <div className="mt-6 -mb-2 rounded-xl bg-violet-500/10 border border-violet-500/30 px-4 py-2.5 text-xs text-violet-300 flex items-center justify-center text-center" data-testid="ai-test-note">
                  <Sparkles className="w-4 h-4 mr-2 shrink-0" />
                  {t("discover.aiTestNote")}
                </div>
              )}
              <div className="grid grid-cols-2 gap-4 mt-6">
                {isPayer && (
                  <Button
                    className="h-14 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold col-span-2"
                    onClick={() => {
                      setSelectedProfile(currentProfile);
                      if (!user?.age_verified) {
                        setAgeModalOpen(true);
                      } else {
                        setSendOfferDialog(true);
                      }
                    }}
                    data-testid="premium-offer-btn"
                  >
                    <Crown className="w-5 h-5 mr-2" />
                    {t("discover.sendOffer")}
                    <span className="ml-2 text-xs opacity-90">{currentProfile.is_ai ? t("common.freeTest") : "$100-$1000"}</span>
                  </Button>
                )}
                
                <Button
                  className="h-14 rounded-xl bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20"
                  onClick={() => navigate(`/profile/${currentProfile.user_id}?action=message`)}
                  data-testid="message-btn"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  {t("discover.message")}
                  {isPayer && (
                    <span className="ml-2 text-xs opacity-70">10 {t("common.credits").toLowerCase()}</span>
                  )}
                </Button>
                <Button
                  className="h-14 rounded-xl gradient-gold text-black font-semibold"
                  onClick={() => navigate(`/profile/${currentProfile.user_id}?action=meetup`)}
                  data-testid="meetup-btn"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  {t("discover.meetUp")}
                  {isPayer && (
                    <span className="ml-2 text-xs opacity-70">{currentProfile.is_ai ? t("common.freeTest") : `25 ${t("common.credits").toLowerCase()}`}</span>
                  )}
                </Button>
              </div>
            </div>
          ) : null}

          {/* Pagination indicator */}
          {profiles.length > 0 && (
            <div className="flex justify-center gap-1 mt-6">
              {profiles.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentIndex ? "bg-primary" : "bg-white/20"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Age Verification Modal */}
      <AgeVerificationModal
        isOpen={ageModalOpen}
        onClose={() => setAgeModalOpen(false)}
        onVerified={() => setSendOfferDialog(true)}
      />

      {/* Boost Dialog */}
      <BoostDialog
        open={boostDialog}
        onOpenChange={(open) => {
          setBoostDialog(open);
          if (!open) fetchBoostStatus();
        }}
        boostStatus={boostStatus}
      />

      {/* Send Offer Modal */}
      <SendOfferModal
        isOpen={sendOfferDialog}
        onClose={() => {
          setSendOfferDialog(false);
          setSelectedProfile(null);
        }}
        woman={selectedProfile}
        onSuccess={() => {
          toast.success("Offer sent! Check your Offers page for status.");
          // Don't refresh profiles, just close modal
        }}
      />
    </div>
  );
}
